import React, { useEffect, useRef } from 'react'
import { Alert, Button, Tooltip, Typography, message as antdMessage } from 'antd'
import { ArrowLeftOutlined, CloseOutlined, HistoryOutlined, PlusOutlined } from '@ant-design/icons'
import { PiMicrosoftTeamsLogo } from 'react-icons/pi'
import { useIntl } from 'react-intl'

import IntlMessages from '../../util/IntlMessages'
import { openSupportChat } from '../../util/open-support-chat'
import { track } from '../../services/analytics/analytics'
import { useAppSelector } from '../../store/hooks'
import { selectAuthUserSlice } from '../../store/auth-user-slice'
import { MSBotSetupDrawerContent } from '../ms-bot-setup-drawer-content'
import { VeeMark } from './vee-mark'
import { VeeHome, VeeFrontDoorMeta } from './vee-home'
import { VeeChats } from './vee-chats'
import { VeeConversation } from './vee-conversation'
import { VeeComposer } from './vee-composer'
import { VeeFooterLinks } from './vee-footer-links'
import { useDeleteVeeConversation, useVeeConversations, VeeChatState, VeeSendOptions, VeeSendOutcome } from './hooks'

export type VeeView = 'home' | 'chats' | 'conversation' | 'botSetup'

interface IVeePanelProps {
  view: VeeView
  setView: (view: VeeView) => void
  chat: VeeChatState
  draft: string
  onDraftChange: (draft: string) => void
  onClose: () => void
}

// An <aside>, not an antd Drawer - no mask or focus trap; Escape/close are our own handlers.
export const VeePanel: React.FC<IVeePanelProps> = ({ view, setView, chat, draft, onDraftChange, onClose }) => {
  const { formatMessage } = useIntl()
  const { authUser } = useAppSelector(selectAuthUserSlice)
  const conversationsQuery = useVeeConversations(view === 'chats')
  const deleteConversation = useDeleteVeeConversation()
  const botSetupReturnView = useRef<VeeView>('home')
  const canSetUpBot = authUser?.role === 'Admin' && authUser?.platform === 'microsoft'

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape' || event.defaultPrevented) {
        return
      }
      // The composer handles its own Escape (blur a non-empty draft) and stops propagation.
      if (document.querySelector('.ant-popover:not(.ant-popover-hidden)')) {
        return
      }
      onClose()
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [onClose])

  const handleSend = async (message: string, options?: VeeSendOptions, meta?: VeeFrontDoorMeta): Promise<VeeSendOutcome> => {
    if (view === 'home') {
      void track('VEE_FRONT_DOOR_PROMPT_SUBMITTED', {
        source: meta ? 'suggestion' : 'typed',
        suggestion_group: meta?.group ?? null,
      })
    }
    const outcome = await chat.send(message, { ...options, newConversation: view === 'home' })
    if (outcome === 'accepted') {
      setView('conversation')
    }
    return outcome
  }

  const handleHomeSend = (message: string, meta?: VeeFrontDoorMeta): Promise<VeeSendOutcome> => handleSend(message, { kbSlug: meta?.kbSlug }, meta)

  const openBotSetup = () => {
    botSetupReturnView.current = view
    setView('botSetup')
  }

  const handleNewChat = () => {
    chat.newChat()
    setView('home')
  }

  const handleOpenConversation = (id: string) => {
    chat.openConversation(id)
    setView('conversation')
  }

  const handleDeleteConversation = async (id: string) => {
    try {
      await deleteConversation(id)
      if (id === chat.conversationId) {
        chat.newChat()
      }
    } catch {
      void antdMessage.error(formatMessage({ id: 'error.somethingWentWrong' }))
    }
  }

  return (
    <aside className="vee-panel" aria-label={formatMessage({ id: 'veeAssistant.name' })}>
      <div className="vee-panel-header">
        {view !== 'home' && (
          <Button
            type="text"
            icon={<ArrowLeftOutlined />}
            onClick={() => {
              if (view === 'botSetup') {
                setView(botSetupReturnView.current)
                return
              }
              setView(view === 'chats' ? (chat.conversationId ? 'conversation' : 'home') : 'home')
            }}
            aria-label={formatMessage({ id: 'app.back' })}
          />
        )}
        <span className="vee-panel-header-identity">
          <VeeMark size={22} />
          <Typography.Text strong>{view === 'chats' ? <IntlMessages id="veeAssistant.yourChats" /> : <IntlMessages id="veeAssistant.name" />}</Typography.Text>
        </span>
        <span className="vee-panel-header-actions">
          {view === 'chats' && (
            <Tooltip title={<IntlMessages id="veeAssistant.newChat" />}>
              <Button type="text" icon={<PlusOutlined />} onClick={handleNewChat} aria-label={formatMessage({ id: 'veeAssistant.newChat' })} />
            </Tooltip>
          )}
          <Tooltip title={<IntlMessages id="veeAssistant.yourChats" />}>
            <Button type="text" icon={<HistoryOutlined />} onClick={() => setView('chats')} aria-label={formatMessage({ id: 'veeAssistant.yourChats' })} />
          </Tooltip>
          {canSetUpBot && (
            <Tooltip title={<IntlMessages id="containers.topbar.setUpTheBot" />}>
              <Button type="text" icon={<PiMicrosoftTeamsLogo />} onClick={openBotSetup} aria-label={formatMessage({ id: 'containers.topbar.setUpTheBot' })} />
            </Tooltip>
          )}
          <Button type="text" icon={<CloseOutlined />} onClick={onClose} aria-label={formatMessage({ id: 'app.close' })} />
        </span>
      </div>

      <div className="vee-panel-body">
        {view === 'home' && <VeeHome onSend={handleHomeSend} />}
        {view === 'botSetup' && <MSBotSetupDrawerContent onClose={() => setView(botSetupReturnView.current)} />}
        {view === 'chats' && (
          <VeeChats
            conversations={conversationsQuery.conversations}
            loading={conversationsQuery.isLoading}
            hasMore={conversationsQuery.hasNextPage}
            loadingMore={conversationsQuery.isFetchingNextPage}
            onShowMore={() => void conversationsQuery.fetchNextPage()}
            deleteDisabledId={chat.waiting ? chat.conversationId : undefined}
            onOpenConversation={handleOpenConversation}
            onNewChat={handleNewChat}
            onDeleteConversation={(id) => void handleDeleteConversation(id)}
          />
        )}
        {view === 'conversation' && (
          <VeeConversation
            messages={chat.messages}
            waiting={chat.waiting}
            pendingUserMessage={chat.pendingUserMessage}
            timedOut={chat.timedOut}
            sendFailed={chat.sendFailed}
            limitReached={chat.limitReached}
            onSend={handleSend}
            onRetry={chat.retry}
          />
        )}
      </div>

      {(view === 'home' || view === 'conversation') && (
        <div className="vee-panel-footer">
          {view === 'home' && chat.limitReached !== null && (
            <Alert type="warning" message={formatMessage({ id: 'bot.agent.dailyLimitReached' }, { limit: chat.limitReached })} showIcon />
          )}
          {view === 'home' && chat.sendFailed && <Alert type="error" message={formatMessage({ id: 'veeAssistant.sendError' })} showIcon />}
          <VeeComposer
            disabled={chat.waiting || chat.historyLoading}
            initialValue={draft}
            onChange={onDraftChange}
            onSend={handleSend}
            placeholderId={view === 'conversation' ? 'veeAssistant.followUpPlaceholder' : 'veeAssistant.placeholder'}
          />
          <VeeFooterLinks />
          {chat.remaining !== null && chat.remaining <= 1 && chat.limitReached === null && (
            <Typography.Text type="secondary" className="vee-panel-disclaimer">
              {chat.remaining === 0 ? <IntlMessages id="bot.agent.lastMessageNotice" /> : <IntlMessages id="bot.agent.messagesLeftWarning" values={{ count: chat.remaining }} />}
            </Typography.Text>
          )}
          <Typography.Text type="secondary" className="vee-panel-disclaimer">
            <IntlMessages
              id="ai.assistant.disclaimer"
              values={{
                support: (...chunks: React.ReactNode[]) => (
                  <a
                    href="#"
                    onClick={(event) => {
                      event.preventDefault()
                      openSupportChat('VEE_ASSISTANT')
                    }}
                  >
                    {chunks}
                  </a>
                ),
              }}
            />
          </Typography.Text>
        </div>
      )}
    </aside>
  )
}
